import React from 'react';
import { Input, Button, Form, Card, message, Select } from 'antd';
import { FormInstance } from 'antd/lib/form';
import BreadcrumbCustom from '../../components/widget/BreadcrumbCustom';
import CustomerManagerService, { CustomerManagerPostParam, CustomerManagerPutParam, ICustomerManagerInfo } from '../../service/customerManager/CustomerManagerService';
import CooperationStatesSelect, { CooperationStates } from '../../components/public/CooperationStatesSelect';
import {
    ADD_CUSTOMER_MANAGER_ERROR_MESSAGE,
    ADD_CUSTOMER_MANAGER_SUCCESS_MESSAGE,
    EDIT_CUSTOMER_MANAGER_ERROR_MESSAGE,
    EDIT_CUSTOMER_MANAGER_SUCCESS_MESSAGE,
} from '../../config/strcfg';
import './page-customer-manager.less';

interface CustomerManagerManagerPageState {
    loading: boolean;
    isEditMode: boolean;
    data: ICustomerManagerInfo | null;
}

class CustomerManagerManagerPage extends React.Component<any, CustomerManagerManagerPageState> {
    state: CustomerManagerManagerPageState = {
        loading: false,
        isEditMode: false,
        data: null,
    };

    formRef = React.createRef<FormInstance>();
    internalPaymentOptions = ['奖金发放', '发票报销'];
    externalPaymentOptions = ['发票报销', '对公转账'];
    isViewMode = () => window.location.hash.startsWith('#/app/customerManager/view');

    async componentDidMount() {
        const searchParams = new URLSearchParams(window.location.hash.split('?')[1]);
        const id = parseInt(searchParams.get('id') || '0');

        if (id) {
            this.setState({ isEditMode: true, loading: true });
            const response = await CustomerManagerService.getCustomerManagerById(id);
            if (response && response.data && response.data.customerManagerInfo) {
                const data = response.data.customerManagerInfo;
                this.setState({ data, loading: false });
                this.formRef.current?.setFieldsValue({
                    managerType: data.managerType,
                    name: data.name,
                    idCardNo: data.idCardNo,
                    residenceCity: data.residenceCity,
                    commissionMode: data.commissionMode,
                    paymentMode: data.paymentMode,
                    states: data.states,
                });
            } else {
                this.setState({ loading: false });
                message.error(response ? response.message : '获取客户经理信息失败');
            }
        }
    }

    handleBackButtonClick = () => {
        window.history.back();
    };

    onFinish = async (params: any) => {
        if (this.isViewMode()) {
            return;
        }
        this.setState({ loading: true });

        if (!this.state.isEditMode) {
            const response = await CustomerManagerService.addExternalCustomerManager(params as CustomerManagerPostParam);
            if (response && response.success) {
                message.success(ADD_CUSTOMER_MANAGER_SUCCESS_MESSAGE);
                this.formRef.current?.resetFields();
            } else {
                message.error(response ? response.message : ADD_CUSTOMER_MANAGER_ERROR_MESSAGE);
            }
            this.setState({ loading: false });
            return;
        }

        const response = await CustomerManagerService.updateCustomerManager({
            ...(params as CustomerManagerPutParam),
            id: this.state.data!.id,
        });
        if (response && response.success) {
            message.success(EDIT_CUSTOMER_MANAGER_SUCCESS_MESSAGE);
            setTimeout(() => window.location.reload(), 500);
        } else {
            message.error(response ? response.message : EDIT_CUSTOMER_MANAGER_ERROR_MESSAGE);
            setTimeout(() => window.location.reload(), 500);
        }
        this.setState({ loading: false });
    };

    render() {
        const { isEditMode, data } = this.state;
        const isViewMode = this.isViewMode();
        const isInternalManager = isEditMode && data?.managerType === 1;
        const paymentOptions = isInternalManager ? this.internalPaymentOptions : this.externalPaymentOptions;

        const initialValues = {
            managerType: isEditMode && data ? data.managerType : 2,
            states: isEditMode && data ? data.states : CooperationStates.normal,
        };

        return (
            <div className={`page-customer-manager_container ${isViewMode ? 'customer-manager-view-mode' : ''}`}>
                <BreadcrumbCustom breads={['客户经理管理', isViewMode ? '查看客户经理' : (isEditMode ? '编辑客户经理' : '新建外部渠道客户经理')]} />
                <Card
                    title={
                        <div className="title-manager_page">
                            <Button type="link" className="back-button" onClick={this.handleBackButtonClick}>{'< 返回'}</Button>
                            {isViewMode ? '查看客户经理' : (isEditMode ? '编辑客户经理' : '新建外部渠道客户经理')}
                        </div>
                    }
                    bordered={false}
                >
                    <div className="customer-manager-form_container">
                        <Form ref={this.formRef} onFinish={this.onFinish} initialValues={initialValues}>
                            <Form.Item label="类型" name="managerType" className="formItem-customer-manager_type">
                                <Select disabled>
                                    <Select.Option value={1}>内部销售</Select.Option>
                                    <Select.Option value={2}>外部渠道</Select.Option>
                                </Select>
                            </Form.Item>

                            {isInternalManager && (
                                <Form.Item label="绑定账号" className="formItem-customer-manager">
                                    <Input value={data?.adminAccount || ''} disabled />
                                </Form.Item>
                            )}

                            <Form.Item label="姓名" name="name" className="formItem-customer-manager" rules={[{ required: true, message: '请输入姓名' }]}>
                                <Input placeholder="请输入姓名" autoComplete="off" disabled={isInternalManager || isViewMode} />
                            </Form.Item>

                            <Form.Item label="身份证" name="idCardNo" className="formItem-customer-manager">
                                <Input placeholder="请输入身份证号码" autoComplete="off" disabled={isViewMode} />
                            </Form.Item>

                            <Form.Item label="居住城市" name="residenceCity" className="formItem-customer-manager">
                                <Input placeholder="请输入居住城市" autoComplete="off" disabled={isViewMode} />
                            </Form.Item>

                            <Form.Item label="提成方式" name="commissionMode" className="formItem-customer-manager">
                                <Input placeholder="请输入提成方式" autoComplete="off" disabled={isViewMode} />
                            </Form.Item>

                            <Form.Item label="支付方式" name="paymentMode" className="formItem-customer-manager">
                                <Select allowClear={!isViewMode} placeholder="请选择支付方式">
                                    {paymentOptions.map((item) => (
                                        <Select.Option key={item} value={item}>{item}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item label="状态" name="states" className="formItem-customer-manager_state">
                                <CooperationStatesSelect showAllOption={false} initialValues={initialValues.states} />
                            </Form.Item>

                            {!isViewMode && (
                                <Form.Item>
                                    <Button type="primary" className="button-customer-manager_submit" htmlType="submit" loading={this.state.loading}>
                                        提交
                                    </Button>
                                </Form.Item>
                            )}
                        </Form>
                    </div>
                </Card>
            </div>
        );
    }
}

export default CustomerManagerManagerPage;
